const ALLOWED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "jpeg",
  "jpg",
  "png",
];

const EXTENSION_MIME_MAP = {
  pdf: ["application/pdf"],
  doc: ["application/msword"],
  docx: [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  xls: ["application/vnd.ms-excel"],
  xlsx: [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ],
  jpeg: ["image/jpeg"],
  jpg: ["image/jpeg"],
  png: ["image/png"],
};

const BYTES_PER_MB = 1000000;

export function getServiceRequestMaxFileSizeMb() {
  const parsed = Number(process.env.SERVICEREQUEST_FILE_UPLOAD_SIZE);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 5;
}

export function getServiceRequestMaxFileSizeBytes() {
  return getServiceRequestMaxFileSizeMb() * BYTES_PER_MB;
}

export function readFileUploadValue(val) {
  if (!val) {
    return { fileUploadId: null, fileUploadItems: [] };
  }
  if (typeof val === "object" && val.fileUploadId != null) {
    return {
      fileUploadId: val.fileUploadId,
      fileUploadItems: Array.isArray(val.fileUploadItems)
        ? val.fileUploadItems
        : [],
    };
  }
  return { fileUploadId: null, fileUploadItems: [] };
}

export function toFileUploadFieldValue({ fileUploadId, fileUploadItems }) {
  const items = Array.isArray(fileUploadItems) ? fileUploadItems : [];
  if (
    (fileUploadId == null || String(fileUploadId).length === 0) &&
    items.length === 0
  ) {
    return null;
  }
  return {
    fileUploadId,
    fileUploadItems: items,
  };
}

export function fileUploadValueFromInitiate(data) {
  if (!data || !Array.isArray(data.fileUploadItems)) {
    return null;
  }
  const fileUploadId =
    data.fieldValue != null && String(data.fieldValue).length > 0
      ? data.fieldValue
      : null;
  if (!fileUploadId) {
    return null;
  }
  return toFileUploadFieldValue({
    fileUploadId,
    fileUploadItems: data.fileUploadItems,
  });
}

export function parseFileUploadResponse(response) {
  const data = response?.data ?? response;
  return {
    fileUploadId: data?.fileUploadId ?? null,
    fileUploadItems: Array.isArray(data?.fileUploadItems)
      ? data.fileUploadItems
      : [],
  };
}

export function mergeFileUploadItems(existing = [], incoming = []) {
  const byId = new Map();

  for (const item of existing) {
    if (item?.fileUploadItemId != null) {
      byId.set(String(item.fileUploadItemId), item);
    }
  }

  for (const item of incoming) {
    if (item?.fileUploadItemId != null) {
      byId.set(String(item.fileUploadItemId), item);
    }
  }

  return Array.from(byId.values());
}

export function applyFileUploadResponse(currentValue, response) {
  const current = readFileUploadValue(currentValue);
  const parsed = parseFileUploadResponse(response);
  const fileUploadId = parsed.fileUploadId ?? current.fileUploadId;
  const fileUploadItems = mergeFileUploadItems(
    current.fileUploadItems,
    parsed.fileUploadItems,
  );
  return toFileUploadFieldValue({ fileUploadId, fileUploadItems });
}

export function removeFileUploadItem(items, itemToRemove) {
  if (!itemToRemove) {
    return Array.isArray(items) ? [...items] : [];
  }

  return (Array.isArray(items) ? items : []).filter((item) => {
    if (
      itemToRemove.fileUploadItemId != null &&
      item.fileUploadItemId != null
    ) {
      return (
        String(item.fileUploadItemId) !==
        String(itemToRemove.fileUploadItemId)
      );
    }
    if (itemToRemove.fileName && item.fileName) {
      return item.fileName !== itemToRemove.fileName;
    }
    return true;
  });
}

export function validateServiceRequestUploadFile(file) {
  if (!file) {
    return { valid: false, message: "Unsupported File Type" };
  }

  const extension = (file.name.split(".").pop() || "").toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return { valid: false, message: "Unsupported File Type" };
  }

  const allowedMimeTypes = EXTENSION_MIME_MAP[extension] || [];
  if (!file.type || !allowedMimeTypes.includes(file.type)) {
    return { valid: false, message: "Unsupported File Type" };
  }

  if (file.size > getServiceRequestMaxFileSizeBytes()) {
    return { valid: false, message: "Attachment too large" };
  }

  return { valid: true };
}

export function isServiceRequestFileUploadValueEmpty(val) {
  const state = readFileUploadValue(val);
  const hasId =
    state.fileUploadId != null && String(state.fileUploadId).length > 0;
  const hasItems = state.fileUploadItems.length > 0;
  return !hasId && !hasItems;
}
